import { DSPalette, DSTypography } from "@clickbank-ui/seller-design-system";
import { ChatBubbleOutlined, KeyboardArrowUp } from "@mui/icons-material";
import { Box, Button, ButtonBase, Card, Collapse, Link, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Trans } from "react-i18next";

const { cbNeutral, cbBlue } = DSPalette;
const { fontWeightReg, fontWeightBld } = DSTypography;

const styles = {
	chatCard: {
		color: "inherit",
		border: `1px solid ${cbNeutral[700]}`,
		p: 3,

		display: "flex",
		gap: 4,
		flexDirection: { xs: "column", md: "row" },
	},
	chatIconContainer: {
		width: 56,
		height: 56,
		borderRadius: "50%",
		bgcolor: cbBlue[680],
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		"& .MuiSvgIcon-root": { color: "white" },
	},
	phoneAvailability: {
		"& *": {
			py: 0.5,
		},
	},
	phoneDetails: {
		display: "flex",
		pt: 1.25,
		gap: { xs: 2, md: 5 },
		flexDirection: { xs: "column", md: "row" },
	},
	phoneNumberCard: {
		bgcolor: cbNeutral[1000],
		borderRadius: 1,
		py: 1,
		px: 2,
		mb: 1,
		width: "fit-content",
		minWidth: 180,
		boxSizing: "border-box",
		a: { textDecoration: "underline" },
	},
	phoneSection: {
		mt: 3,
		px: 1.5,
	},
	phoneToggle: {
		gap: 1.5,
	},
};

const ContactCardsPanel = () => {
	const [phoneExpanded, setPhoneExpanded] = useState(false);
	const [zoomCampaignSdk, setZoomCampainSdk] = useState<ZoomCampaignSdk>();

	useEffect(() => {
		const hash = window.location.hash;
		if (hash === "#contactUs") {
			const element = document.getElementById(hash.substring(1));
			setTimeout(() => {
				element?.scrollIntoView({ behavior: "smooth", block: "start" });
			}, 1000);
		}

		return window.waitForZoomCampainSdkReady(setZoomCampainSdk);
	}, []);

	return (
		<Stack spacing={3}>
			<Typography variant="h2">
				<Trans i18nKey="Common.contactUs">Contact Us</Trans>
			</Typography>

			<Typography variant="body1">
				<Trans i18nKey="ContactCardsPanel.intro">
					If you have questions about your purchase or need additional support, please
					contact our Customer Support team to speak with our friendly agents.
				</Trans>
			</Typography>

			{/* Chat Card */}
			<Card sx={styles.chatCard}>
				{/* Left Side - Chat Action */}
				<Stack flex={1} spacing={2}>
					<Stack gap={1.5} direction="row">
						<Box sx={styles.chatIconContainer}>
							<ChatBubbleOutlined />
						</Box>
						<Box>
							<Typography variant="h4" fontWeight={700}>
								<Trans i18nKey="ContactCardsPanel.needHelp">Need Help?</Trans>
							</Typography>
							<Box>
								<Typography
									variant="body2"
									fontWeight={fontWeightBld}
									marginTop={0.5}
								>
									<Trans i18nKey="ContactCardsPanel.hereForYou">
										We&apos;re here for you
									</Trans>
								</Typography>
							</Box>
						</Box>
					</Stack>
					<Button
						id="contactUs"
						variant="contained"
						color="primary"
						fullWidth
						disabled={!zoomCampaignSdk}
						aria-label="Start live chat"
						onClick={() => zoomCampaignSdk?.open()}
					>
						<Trans i18nKey="Common.chatNow">Chat Now</Trans>
					</Button>
				</Stack>

				{/* Right Side - Hours Info */}
				<Box flex={1}>
					<Typography variant="h6">
						<Trans i18nKey="ContactCardsPanel.chatHours">5 AM to 7 PM MST</Trans>
					</Typography>
					<Typography variant="caption" as="p" fontWeight={fontWeightReg}>
						<Trans i18nKey="ContactCardsPanel.chatDays">7 Days a Week</Trans>{" "}
						<Box component="span" fontWeight={600}>
							<Trans i18nKey="ContactCardsPanel.contactDaysException">
								(Except major holidays)
							</Trans>
						</Box>
					</Typography>

					<Typography
						variant="caption"
						as="p"
						pt={1}
						fontWeight={fontWeightReg}
						fontStyle="italic"
					>
						<Trans i18nKey="ContactCardsPanel.languagesSupported">
							Languages Supported:
						</Trans>
					</Typography>
					<Typography variant="caption" fontWeight={fontWeightReg}>
						<Trans i18nKey="ContactCardsPanel.enEsFrPtDe">
							English, Spanish, French, Portuguese, German
						</Trans>
					</Typography>

					<Typography variant="caption" as="p" pt={1} fontWeight={600} lineHeight="1rem">
						<Trans i18nKey="ContactCardsPanel.chatFastest">
							Live chat is the fastest way to resolve your questions and issues.
						</Trans>
					</Typography>
				</Box>
			</Card>

			{/* Phone Support Section */}
			<Box sx={styles.phoneSection}>
				<Typography variant="body3" marginBottom={2} fontWeight={fontWeightReg}>
					<Trans i18nKey="ContactCardsPanel.preferAnotherMethod">
						Prefer another contact method? We also offer phone support.
					</Trans>
				</Typography>
				<ButtonBase
					onClick={() => setPhoneExpanded(!phoneExpanded)}
					sx={styles.phoneToggle}
					aria-expanded={phoneExpanded}
					aria-controls="phone-support-content"
				>
					<Typography variant="body3" fontWeight={500}>
						<Trans i18nKey="ContactCardsPanel.showPhoneOptions">
							Show Phone Support Options
						</Trans>
					</Typography>
					<KeyboardArrowUp
						sx={{ fontSize: 24, transform: phoneExpanded ? "none" : "rotate(180deg)" }}
					/>
				</ButtonBase>
				<Collapse in={phoneExpanded} id="phone-support-content">
					<Box sx={styles.phoneDetails}>
						{/* Phone Hours Info */}
						<Box sx={styles.phoneAvailability}>
							<Typography variant="h4">
								<Trans i18nKey="Common.callUs">Call Us</Trans>
							</Typography>
							<Typography variant="h6" fontWeight={500}>
								<Trans i18nKey="ContactCardsPanel.phoneAvailability">
									Available 7 AM to 5 PM MST
								</Trans>
							</Typography>
							<Typography variant="caption" as="p" fontWeight={fontWeightReg}>
								<Trans i18nKey="ContactCardsPanel.phoneDays">7 Days a Week</Trans>{" "}
								<Trans i18nKey="ContactCardsPanel.contactDaysException">
									(Except major holidays)
								</Trans>
							</Typography>
							<Typography variant="caption" fontWeight={fontWeightReg}>
								<Trans i18nKey="ContactCardsPanel.languagesSupported">
									Languages Supported:
								</Trans>{" "}
								<Trans i18nKey="ContactCardsPanel.enEs">English, Spanish</Trans>
							</Typography>
						</Box>

						{/* Phone Number Cards */}
						<Box>
							<Box sx={styles.phoneNumberCard}>
								<Typography variant="caption" as="p" fontWeight={fontWeightReg}>
									<Trans i18nKey="ContactCardsPanel.tollFreeUS">
										Toll Free (US):
									</Trans>
								</Typography>
								<Link href="tel:1-800-390-6035">+1 800 390-6035</Link>
							</Box>
							<Box sx={styles.phoneNumberCard}>
								<Typography variant="caption" as="p" fontWeight={fontWeightReg}>
									<Trans i18nKey="Common.international">International:</Trans>
								</Typography>
								<Link href="tel:+1-208-345-4245">+1 208 345-4245</Link>
							</Box>
						</Box>
					</Box>
				</Collapse>
			</Box>
		</Stack>
	);
};

export default ContactCardsPanel;
